/**
 * Turns the `.blog-categories` block into an accessible dropdown.
 *
 * Expected HTML structure:
 * <div class="blog-categories">
 *   <h3 class="blog-categories__title">Categories</h3>
 *   <ul class="blog-categories__list">
 *     <li><a class="blog-categories__link" href="...">All Categories</a></li>
 *   </ul>
 * </div>
 */
export class BlogCategories {
  private static readonly INITIALIZED_ATTR = 'data-blog-categories-initialized';
  private static readonly ACTIVE_MODIFIER = 'blog-categories--active';
  private containers: HTMLElement[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    this.containers = Array.from(document.querySelectorAll('.blog-categories'));

    this.containers.forEach((container, idx) => {
      if (container.getAttribute(BlogCategories.INITIALIZED_ATTR) === 'true') {
        return;
      }

      const toggle = container.querySelector('.blog-categories__title') as HTMLElement | null;
      const menu = container.querySelector('.blog-categories__list') as HTMLElement | null;

      if (!toggle || !menu) {
        return;
      }

      if (!menu.id) {
        menu.id = `blogCategoriesMenu_${idx}`;
      }

      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', menu.id);

      const closeMenu = (): void => {
        container.classList.remove(BlogCategories.ACTIVE_MODIFIER);
        toggle.setAttribute('aria-expanded', 'false');
      };

      const openMenu = (): void => {
        this.containers.forEach((otherContainer) => {
          if (otherContainer !== container) {
            otherContainer.classList.remove(BlogCategories.ACTIVE_MODIFIER);
            otherContainer.querySelector('.blog-categories__title')?.setAttribute('aria-expanded', 'false');
          }
        });
        container.classList.add(BlogCategories.ACTIVE_MODIFIER);
        toggle.setAttribute('aria-expanded', 'true');
      };

      const toggleMenu = (): void => {
        if (container.classList.contains(BlogCategories.ACTIVE_MODIFIER)) {
          closeMenu();
        } else {
          openMenu();
        }
      };

      toggle.addEventListener('click', toggleMenu);

      toggle.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleMenu();
        } else if (e.key === 'Escape') {
          closeMenu();
          toggle.focus();
        }
      });

      document.addEventListener('click', (e: MouseEvent) => {
        if (!container.contains(e.target as Node)) {
          closeMenu();
        }
      });

      container.setAttribute(BlogCategories.INITIALIZED_ATTR, 'true');
    });
  }
}
